/**
 * ForgotPasswordPage — 21 CFR Part 11 Self-Service Password Reset
 * Step 1: Enter company ID + username → receive OTP
 * Step 2: Enter OTP + new password
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Smartphone, KeyRound, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, RefreshCw, Building2, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button }    from '../../components/ui/button';
import { Input }     from '../../components/ui/input';
import { Label }     from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useTheme }  from '../context/ThemeContext';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import FloatingParticles from '../components/FloatingParticles';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { API_BASE, API } from '../../lib/api';

const OTP_LEN = 6;

function OtpInput({ value, onChange, disabled }) {
  const refs = Array.from({ length: OTP_LEN }, () => React.createRef());
  const handle = (i, v) => {
    const digits = value.split('');
    digits[i] = v.replace(/\D/,'').slice(-1);
    onChange(digits.join('').slice(0, OTP_LEN));
    if (v && i < OTP_LEN-1) refs[i+1]?.current?.focus();
  };
  const handleKey = (i, e) => {
    if (e.key==='Backspace' && !value[i] && i>0) refs[i-1]?.current?.focus();
  };
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({length:OTP_LEN},(_,i)=>(
        <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1} value={value[i]||''}
          onChange={e=>handle(i,e.target.value)} onKeyDown={e=>handleKey(i,e)} disabled={disabled}
          className={`w-11 h-12 text-center text-lg font-bold rounded-xl border-2 outline-none transition-colors dark:bg-white/[0.06] bg-white dark:text-white text-gray-900 disabled:opacity-40 ${value[i]?'dark:border-[#3DD56D] border-[#3DD56D]':'dark:border-white/[0.15] border-gray-200'} focus:dark:border-[#3DD56D] focus:border-[#3DD56D]`}
          style={{ fontFamily:"'IBM Plex Mono',monospace" }} />
      ))}
    </div>
  );
}

export default function ForgotPasswordPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [step, setStep]           = useState(1); // 1=request, 2=verify, 3=done
  const [channel, setChannel]     = useState('email');
  const [clientId, setClientId]   = useState('');
  const [username, setUsername]   = useState('');
  const [otp, setOtp]             = useState('');
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirmPwd]= useState('');
  const [showNew, setShowNew]     = useState(false);
  const [showConf, setShowConf]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [devOtp, setDevOtp]       = useState('');

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c-1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Step 1: Request OTP
  const handleRequest = async (e) => {
    e.preventDefault();
    if (!clientId.trim() || !username.trim()) { toast.error('Company ID and username are required.'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password/request`, { client_id:clientId.trim(), username:username.trim(), channel });
      toast.success(data.message);
      if (data.dev_otp) {
        setDevOtp(data.dev_otp);
        toast.info(`Dev OTP: ${data.dev_otp}`, { duration: 30000 });
      }
      setCountdown(data.expires_in_mins * 60 || 900);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send OTP.');
    } finally { setLoading(false); }
  };

  // Step 2: Verify OTP + set new password
  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length < OTP_LEN) { toast.error('Enter the complete 6-digit OTP.'); return; }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/verify`, { client_id:clientId, username, otp, new_password:newPwd, confirm_password:confirmPwd });
      toast.success('Password reset successfully!');
      setStep(3);
    } catch (err) {
      const details = err.response?.data?.details;
      toast.error(err.response?.data?.error || 'OTP verification failed.');
      if (details) details.forEach(d => toast.error(d, { duration:6000 }));
    } finally { setLoading(false); }
  };

  const mins = Math.floor(countdown/60);
  const secs = countdown % 60;

  return (
    <div className="min-h-screen dark:bg-[#080808] bg-slate-50 flex flex-col relative overflow-hidden">
      {isDark && <FloatingParticles />}

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-5 py-4 dark:border-b dark:border-white/[0.06] border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/login')} className="w-8 h-8 rounded-lg dark:bg-white/[0.05] bg-white border dark:border-white/[0.09] border-gray-200 flex items-center justify-center dark:text-neutral-400 text-gray-500 dark:hover:text-white hover:text-gray-900 transition-colors">
            <ArrowLeft size={14} />
          </button>
          <RetailCliqLogo size="sm" />
        </div>
        <ThemeToggle />
      </div>

      {/* Main */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-5">
        <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ duration:.35 }} className="w-full max-w-md">
          <AnimatePresence mode="wait">

            {/* STEP 1 — Request OTP */}
            {step === 1 && (
              <motion.div key="step1" initial={{opacity:0,x:10}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-10}} transition={{duration:.2}}
                className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#3DD56D]/10 border border-[#3DD56D]/20 flex items-center justify-center">
                    <KeyRound size={18} className="text-[#3DD56D]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold dark:text-white text-gray-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Reset Password</h2>
                    <p className="text-xs dark:text-neutral-400 text-gray-500">Enter your company and username</p>
                  </div>
                </div>

                {/* Channel select */}
                <Tabs value={channel} onValueChange={setChannel} className="mb-4">
                  <TabsList className="w-full dark:bg-white/[0.05] bg-gray-100 p-0.5 rounded-lg h-auto">
                    <TabsTrigger value="email" className="flex-1 flex items-center gap-1.5 text-[11px] py-1.5 rounded-md dark:data-[state=active]:bg-white/[0.12] data-[state=active]:bg-white dark:data-[state=active]:text-white data-[state=active]:text-gray-900 dark:text-neutral-400 text-gray-500">
                      <Mail size={12}/> Email OTP
                    </TabsTrigger>
                    <TabsTrigger value="sms" className="flex-1 flex items-center gap-1.5 text-[11px] py-1.5 rounded-md dark:data-[state=active]:bg-white/[0.12] data-[state=active]:bg-white dark:data-[state=active]:text-white data-[state=active]:text-gray-900 dark:text-neutral-400 text-gray-500">
                      <Smartphone size={12}/> SMS OTP
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <form onSubmit={handleRequest} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">Company ID</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13}/>
                      <Input placeholder="e.g. MAE10005" value={clientId} onChange={e=>setClientId(e.target.value)} className="pl-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13}/>
                      <Input placeholder="Your username" value={username} onChange={e=>setUsername(e.target.value)} className="pl-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-9 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_4px_16px_rgba(61,213,109,0.22)] disabled:opacity-50 text-sm">
                    {loading ? <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin"/>Sending...</span> : `Send OTP via ${channel==='email'?'Email':'SMS'}`}
                  </Button>
                </form>
                <p className="text-center mt-4 text-xs dark:text-neutral-500 text-gray-400"><a href="/login" className="text-[#3DD56D] hover:text-[#5fe88a] font-medium">Back to Login</a></p>
              </motion.div>
            )}

            {/* STEP 2 — Verify OTP + new password */}
            {step === 2 && (
              <motion.div key="step2" initial={{opacity:0,x:10}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-10}} transition={{duration:.2}}
                className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-6">
                <div className="text-center mb-5">
                  <h2 className="text-lg font-bold dark:text-white text-gray-900 mb-1" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Enter OTP</h2>
                  <p className="text-xs dark:text-neutral-400 text-gray-500">OTP sent to your registered {channel}. Expires in {mins}:{String(secs).padStart(2,'0')}</p>
                </div>

                <form onSubmit={handleVerify} className="space-y-4">
                  {devOtp && (
                    <div className="dark:bg-[#F5C451]/[0.08] bg-[#F5C451]/[0.08] border dark:border-[#F5C451]/20 border-[#F5C451]/20 rounded-xl px-3 py-2 text-center">
                      <p className="text-[11px] dark:text-neutral-400 text-gray-500">Dev OTP: <span className="font-bold text-[#F5C451] tracking-widest" style={{fontFamily:"'IBM Plex Mono',monospace"}}>{devOtp}</span></p>
                    </div>
                  )}

                  <OtpInput value={otp} onChange={setOtp} disabled={countdown<=0} />

                  <div className="space-y-1.5">
                    <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13}/>
                      <Input type={showNew?'text':'password'} placeholder="New password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} className="pl-9 pr-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                      <button type="button" onClick={()=>setShowNew(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400">{showNew?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                    </div>
                    <PasswordStrengthMeter password={newPwd} />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13}/>
                      <Input type={showConf?'text':'password'} placeholder="Repeat password" value={confirmPwd} onChange={e=>setConfirmPwd(e.target.value)} className={`pl-9 pr-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:text-white text-gray-900 focus-visible:ring-1 ${confirmPwd&&newPwd!==confirmPwd?'border-[#FF5A6A] focus-visible:ring-[#FF5A6A]':'dark:border-white/[0.09] border-gray-200 focus-visible:ring-[#3DD56D]'}`} />
                      <button type="button" onClick={()=>setShowConf(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400">{showConf?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" disabled={countdown>0} onClick={()=>{ setOtp(''); setStep(1); setDevOtp(''); }} className="flex-none h-9 px-3 rounded-xl dark:text-neutral-400 text-gray-500 text-sm">
                      <RefreshCw size={13} className="mr-1"/> Resend
                    </Button>
                    <Button type="submit" disabled={loading||otp.length<OTP_LEN||countdown<=0} className="flex-1 h-9 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold disabled:opacity-50 text-sm">
                      {loading?<span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin"/>Verifying...</span>:'Reset Password'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* STEP 3 — Success */}
            {step === 3 && (
              <motion.div key="step3" initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.3}}
                className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#3DD56D]/10 border-2 border-[#3DD56D]/25 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={30} className="text-[#3DD56D]" />
                </div>
                <h2 className="text-lg font-bold dark:text-white text-gray-900 mb-2" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Password Reset!</h2>
                <p className="dark:text-neutral-400 text-gray-500 text-sm mb-5">Your password has been reset successfully. Please login with your new password.</p>
                <Button onClick={()=>navigate('/login')} className="w-full h-9 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold text-sm">Back to Login</Button>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
